import { ErrorResponseBody } from '@/@types/common'
import useUniversalNavigate from '@/utils/hooks/useUniversalNavigate'
import { useQueryErrorResetBoundary } from '@tanstack/react-query'
import { isAxiosError } from 'axios'
import clsx from 'clsx'
import { type HTMLAttributes, useEffect, useState } from 'react'
import { z } from 'zod'
import { Button } from '@mantine/core'
import styles from './ErrorBoundary.module.css'

const InfoSchema = z.object({
	title: z.string(),
	desc: z.string(),
})
type Info = z.infer<typeof InfoSchema>

export interface ErrorBoundaryProps extends HTMLAttributes<HTMLDivElement> {
	error: Error
	reset: () => void
}

export default function ErrorBoundary({ error, reset, className, ...props }: ErrorBoundaryProps) {
	const queryErrorResetBoundary = useQueryErrorResetBoundary()
	const navigate = useUniversalNavigate()
	const [info, setInfo] = useState<Info>({
		title: 'Something went wrong',
		desc: error.message,
	})

	useEffect(() => {
		queryErrorResetBoundary.reset()
	}, [queryErrorResetBoundary])

	useEffect(() => {
		if (isAxiosError<ErrorResponseBody>(error)) {
			if (error.response?.data.error) {
				setInfo({
					title: error.response?.data.error || 'Something went wrong',
					desc: error.message,
				})
			}
		}
	}, [error])

	return (
		<div {...props} className={clsx(styles.root, className)}>
			<div className={styles.title}>
				{info.title}
				<br />
				<div className={styles.desc}>{info.desc}</div>
			</div>
			<div className={styles.actions}>
				<Button
					variant="outline"
					size="sm"
					onClick={() => {
						reset()
					}}
				>
					重试
				</Button>
				<Button size="sm" onClick={(e) => navigate({ to: '/' }, e)}>
					返回主页
				</Button>
			</div>
		</div>
	)
}
